/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useMemo } from 'react';
import { Platform, View, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useLogger } from '@airasia-rn/core-logger';
import { openInAppBrowser } from '@airasia-rn/ui';
import { HotelBanner } from '../../components/HotelBanner';
import { SkeletonLoaderWrapper } from '../../components/SkeletonLoaderWrapper';
import { useHotelBanners } from '../../hooks';
import { parseBannerData, getDefaultBanner } from './utils';
import type { HotelBannerItem } from '../../types';
import { pd } from '@airasia-phoenix/react-native/utils';
import {
  useOptionalHotelsNavigation,
  useOptionalHotelsRoutes,
} from '../../context/HotelsNavigationContext';
import { getResponsiveBannerHeight } from '../../utils/hotelBannerHeight';
import { resolveBannerRedirection } from '../../utils';
import { SearchWidgetEvent } from '../../analytics';

interface HotelBannerProps {
  countryCode?: string;
  testID?: string;
}

/**
 * HotelSearchBanner - Widget that fetches and displays hotel banners
 * Handles data fetching with useHotelBanner hook and skeleton loading state
 */

const HotelBannerView = ({
  countryCode,
  testID = 'hotel-banner',
}: HotelBannerProps) => {
  const { width: screenWidth } = useWindowDimensions();
  const { logEvent } = useLogger();
  // Optional: the home package renders this view without the provider.
  const navigation = useOptionalHotelsNavigation();
  const routes = useOptionalHotelsRoutes();
  const bannerHeight = getResponsiveBannerHeight(screenWidth);

  const { data: bannerData, isLoading, isError } = useHotelBanners(countryCode);

  // Memoize banners to avoid recalculating on every render
  const banners = useMemo(() => {
    const fallbackBanner = getDefaultBanner();
    if (isError && !bannerData) {
      return [fallbackBanner];
    }

    const parsedBanners = parseBannerData(bannerData, countryCode);
    if (parsedBanners.length === 0) {
      return [fallbackBanner];
    }

    return parsedBanners;
  }, [bannerData, countryCode, isError]);

  // Handle banner view tracking - fires when each banner becomes visible
  const handleBannerView = useCallback(
    (item: HotelBannerItem, index: number) => {
      const params = SearchWidgetEvent.hotelBannerViewEvent({
        bannerName: item.name || item.id,
        position: index + 1,
      });
      logEvent('view', params);
    },
    [logEvent],
  );

  // Handle banner tap tracking
  const handleBannerPress = useCallback(
    (item: HotelBannerItem, index: number) => {
      if (!item.redirectionUrl) return;

      const params = SearchWidgetEvent.hotelBannerTapEvent({
        bannerName: item.name || item.id,
        position: index + 1,
      });
      logEvent('tap', params);

      const redirection = resolveBannerRedirection(item.redirectionUrl);
      if (redirection.type === 'hotel-search') {
        const listPath = routes?.HOTELS_LIST ?? '/';
        const listUrl = `${listPath}${redirection.queryString}`;

        if (navigation) {
          navigation.navigateToListUrl(listUrl);
        } else if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.location.assign(
            listUrl.startsWith('http')
              ? listUrl
              : `${window.location.origin}${listUrl.startsWith('/') ? listUrl : `/${listUrl}`}`,
          );
        } else {
          const { router: nativeRouter } = require('expo-router');
          nativeRouter.push(listUrl);
        }
      } else {
        openInAppBrowser(item.redirectionUrl);
      }
    },
    [logEvent, navigation, routes?.HOTELS_LIST],
  );

  // Show skeleton loader while loading
  if (isLoading) {
    return (
      <SkeletonLoaderWrapper
        testID={`${testID}-skeleton`}
        height={bannerHeight}
        itemWidth={screenWidth}
        containerStyle={styles.skeletonContainer}
        borderRadiusShape={pd.sys.shape.corner.medium}
      />
    );
  }

  return (
    <View testID={testID} style={styles.container}>
      <HotelBanner
        banners={banners}
        testID={`${testID}-carousel`}
        onBannerPress={handleBannerPress}
        onBannerView={handleBannerView}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  skeletonContainer: {
    width: '100%',
    paddingHorizontal: 0,
  },
});

export default HotelBannerView;
